import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "bg-white rounded-lg border border-gray-200 shadow-sm"
}
const _hoisted_2 = ["title"]
const _hoisted_3 = { class: "flex items-center gap-2" }
const _hoisted_4 = ["title"]
const _hoisted_5 = ["checked", "onChange"]
const _hoisted_6 = ["onClick"]
const _hoisted_7 = ["onClick"]
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  key: 1,
  class: "bg-white rounded-lg border border-gray-200 p-8 text-center"
}
const _hoisted_10 = { class: "text-center py-4" }
const _hoisted_11 = { class: "text-sm text-black mb-4" }
const _hoisted_12 = { class: "flex justify-end space-x-3" }
const _hoisted_13 = { class: "text-sm text-black space-y-2" }
const _hoisted_14 = { class: "text-black" }
const _hoisted_15 = { class: "flex justify-end space-x-3" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = ["disabled"]
const _hoisted_18 = {
  key: 0,
  class: "animate-spin h-4 w-4 text-white",
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  viewBox: "0 0 24 24"
}

import { ref } from 'vue'
import { ReusableTable, ModalComp } from '@/components'
import type { PromoCode, PromoCodeTableProps, PromoCodeTableEmits } from '@/types/promo'
import { sendPromoEmail } from '@/api/promoCodes'
import { useAuthStore } from '@/stores/auth'
import { useNotification } from '@/composables/useNotification'


export default /*@__PURE__*/_defineComponent({
  __name: 'PromoCodeTable',
  props: {
    promoCodes: {}
  },
  emits: ["delete", "toggle-status", "view"],
  setup(__props: any, { emit: __emit }) {


const emit = __emit
const { success, error: showError } = useNotification()
const auth = useAuthStore()

// État de la modal de suppression
const showDeleteModal = ref(false)
const promoToDelete = ref<PromoCode | null>(null)
const showSendModal = ref(false)
const promoToSend = ref<PromoCode | null>(null)
const isSendingEmail = ref(false)

// Table columns
const promoCodeColumns = [
  { key: 'code', title: 'CODE PROMO', sortable: true, align: 'left' as const },
  { key: 'acheteur', title: 'Acheteur', sortable: true, align: 'left' as const },
  { key: 'type', title: 'Type', sortable: true, align: 'left' as const },
  { key: 'montant', title: 'Montant', sortable: true, align: 'right' as const },
  { key: 'montantMin', title: 'Minimum', sortable: true, align: 'right' as const },
  { key: 'dateDebut', title: 'Date début', sortable: true, align: 'left' as const },
  { key: 'dateFin', title: 'Date fin', sortable: true, align: 'left' as const },
  { key: 'statut', title: 'Statut', sortable: false, align: 'left' as const }
]

// ===== Helpers Statut =====
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

// La colonne affiche jj/mm/aaaa. new Date("04/12/2026") le lit comme le 12 avril.
const parsePromoDate = (iso?: string | null, displayed?: string) => {
  if (iso) {
    const fromIso = new Date(iso)
    if (!isNaN(fromIso.getTime())) return fromIso
  }
  if (!displayed || displayed === 'Non définie') return null
  const french = displayed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (french) {
    return new Date(Number(french[3]), Number(french[2]) - 1, Number(french[1]))
  }
  const fallback = new Date(displayed)
  return isNaN(fallback.getTime()) ? null : fallback
}

const statusLabel = (row: PromoCode) => {
  const isManuallyActive = (row.actif || '').toLowerCase() === 'activé'
  if (!isManuallyActive) return 'Désactivé'

  const today = startOfDay(new Date())
  const start = parsePromoDate(row.dateDebutISO, row.dateDebut)
  const end = parsePromoDate(row.dateFinISO, row.dateFin)

  const startsOk = !start || startOfDay(start) <= today
  const endsOk = !end || today <= startOfDay(end)

  if (startsOk && endsOk) return 'Actif aujourd’hui'
  if (start && startOfDay(start) > today) return 'À venir'
  return 'Expiré'
}

const statusBadgeClass = (row: PromoCode) => {
  const label = statusLabel(row)
  switch (label) {
    case 'Actif aujourd’hui':
      return 'bg-green-100 text-green-800'
    case 'À venir':
      return 'bg-blue-100 text-blue-800'
    case 'Expiré':
      return 'bg-gray-100 text-black'
    default:
      return 'bg-red-100 text-red-700'
  }
}

const statusIcon = (row: PromoCode) => {
  const label = statusLabel(row)
  switch (label) {
    case 'Actif aujourd’hui':
      return 'fas fa-check-circle'
    case 'À venir':
      return 'fas fa-clock'
    case 'Expiré':
      return 'fas fa-hourglass-end'
    default:
      return 'fas fa-ban'
  }
}

const statusTitle = (row: PromoCode) => {
  const start = row.dateDebut || '—'
  const end = row.dateFin || '—'
  return `Période: ${start} → ${end}`
}

const handleDeletePromoCode = (row: PromoCode) => {
  promoToDelete.value = row
  showDeleteModal.value = true
}

const confirmDelete = () => {
  if (promoToDelete.value) {
    emit('delete', promoToDelete.value)
    showDeleteModal.value = false
    promoToDelete.value = null
  }
}

const handleTogglePromoCodeStatus = (row: PromoCode) => {
  emit('toggle-status', row)
}

const viewPromoCode = (row: PromoCode) => {
  emit('view', row)
}

const openSendEmail = (row: PromoCode) => {
  promoToSend.value = row
  showSendModal.value = true
  isSendingEmail.value = false
}

const confirmSend = async () => {
  if (isSendingEmail.value) return
  if (!promoToSend.value?.buyerEmail) {
    showError('Email acheteur introuvable')
    showSendModal.value = false
    return
  }
  try {
    isSendingEmail.value = true
    // Construire les infos nécessaires pour l'email promo
    const buyerFirstName = (promoToSend.value.acheteur || '').split(' ')[0] || 'Client'
    const sellerName = auth.user?.nomBoutique || 'Votre grossiste'
    // discountLabel dérivé du montant/type (ex: "-20%" ou "30€")
    // Conserver les décimales (ex: 1.05% ne doit pas devenir 105%)
    const montantStr = String(promoToSend.value.montant)
    const montantMatch = montantStr.match(/\d+(?:[.,]\d+)?/)
    const montantLabel = (montantMatch ? montantMatch[0] : '').replace(',', '.')
    const discountLabel = promoToSend.value.type === 'Percentage'
      ? `-${montantLabel}%`
      : `${montantLabel}€`
    const promoCode = promoToSend.value.code
    const endDate = promoToSend.value.dateFin || '—'
    const catalogueUrl = window.location.origin

    const ok = await sendPromoEmail({
      to: [promoToSend.value.buyerEmail],
      buyerFirstName,
      sellerName,
      discountLabel,
      promoCode,
      endDate,
      catalogueUrl,
    })
    if (ok) {
      success('Email envoyé avec succès')
    } else {
      showError('Échec de l\'envoi de l\'email')
    }
  } catch (e) {
    showError('Erreur lors de l\'envoi de l\'email')
  } finally {
    isSendingEmail.value = false
    showSendModal.value = false
    promoToSend.value = null
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_ctx.promoCodes.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(_unref(ReusableTable), {
            columns: promoCodeColumns,
            data: _ctx.promoCodes,
            sortable: true,
            "show-edit": true,
            "show-delete": false
          }, {
            "column-statut": _withCtx(({ row }) => [
              _createElementVNode("span", {
                class: _normalizeClass(['inline-flex items-center px-2 py-1 rounded text-xs font-medium', statusBadgeClass(row)]),
                title: statusTitle(row)
              }, [
                _createElementVNode("i", {
                  class: _normalizeClass([statusIcon(row), "mr-1"])
                }, null, 2),
                _createTextVNode(" " + _toDisplayString(statusLabel(row)), 1)
              ], 10, _hoisted_2)
            ]),
            actions: _withCtx(({ row }) => [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("label", {
                  class: "switch",
                  title: row.actif === 'activé' ? 'Désactiver' : 'Activer'
                }, [
                  _createElementVNode("input", {
                    type: "checkbox",
                    checked: row.actif === 'activé',
                    onChange: ($event: any) => (handleTogglePromoCodeStatus(row))
                  }, null, 40, _hoisted_5),
                  _cache[4] || (_cache[4] = _createElementVNode("span", { class: "slider" }, null, -1))
                ], 8, _hoisted_4),
                _createElementVNode("button", {
                  onClick: _withModifiers(($event: any) => (viewPromoCode(row)), ["stop"]),
                  class: "w-8 h-8 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center justify-center",
                  title: "Voir"
                }, _cache[5] || (_cache[5] = [
                  _createElementVNode("i", { class: "fas fa-eye text-sm" }, null, -1)
                ]), 8, _hoisted_6),
                (row.buyerEmail && row.acheteur !== 'Tous les acheteurs')
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      onClick: _withModifiers(($event: any) => (openSendEmail(row)), ["stop"]),
                      class: "w-8 h-8 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center justify-center",
                      title: "Envoyer un email à cet acheteur"
                    }, _cache[6] || (_cache[6] = [
                      _createElementVNode("i", { class: "fas fa-envelope text-sm" }, null, -1)
                    ]), 8, _hoisted_7))
                  : _createCommentVNode("", true),
                _createElementVNode("button", {
                  onClick: _withModifiers(($event: any) => (handleDeletePromoCode(row)), ["stop"]),
                  class: "w-8 h-8 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center justify-center",
                  title: "Supprimer"
                }, _cache[7] || (_cache[7] = [
                  _createElementVNode("i", { class: "fas fa-trash text-sm" }, null, -1)
                ]), 8, _hoisted_8)
              ])
            ]),
            _: 1
          }, 8, ["data"])
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_9, _cache[8] || (_cache[8] = [
          _createElementVNode("div", { class: "w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" }, [
            _createElementVNode("i", { class: "fas fa-ticket-alt text-black text-2xl" })
          ], -1),
          _createElementVNode("p", { class: "text-black mb-2" }, "Vous n'avez aucun code promo personnalisé.", -1),
          _createElementVNode("p", { class: "text-sm text-black" }, "Créez votre premier code promo pour commencer.", -1)
        ]))),
    _createVNode(_unref(ModalComp), {
      isVisible: showDeleteModal.value,
      title: "Confirmer la suppression",
      size: "md",
      "onUpdate:isVisible": _cache[1] || (_cache[1] = ($event: any) => (showDeleteModal.value = false))
    }, {
      body: _withCtx(() => [
        _createElementVNode("div", _hoisted_10, [
          _cache[11] || (_cache[11] = _createElementVNode("div", { class: "mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4" }, [
            _createElementVNode("i", { class: "fas fa-exclamation-triangle text-red-600 text-xl" })
          ], -1)),
          _cache[12] || (_cache[12] = _createElementVNode("h3", { class: "text-lg font-medium text-black mb-2" }, " Supprimer le code promo ", -1)),
          _createElementVNode("p", _hoisted_11, [
            _cache[9] || (_cache[9] = _createTextVNode(" Êtes-vous sûr de vouloir supprimer le code promo ")),
            _createElementVNode("strong", null, "\"" + _toDisplayString(promoToDelete.value?.code) + "\"", 1),
            _cache[10] || (_cache[10] = _createTextVNode(" ? "))
          ]),
          _cache[13] || (_cache[13] = _createElementVNode("p", { class: "text-xs text-black" }, " Cette action est irréversible. ", -1))
        ])
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_12, [
          _createElementVNode("button", {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (showDeleteModal.value = false)),
            type: "button",
            class: "px-4 py-2 text-black bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none transition-colors"
          }, " Annuler "),
          _createElementVNode("button", {
            onClick: confirmDelete,
            type: "button",
            class: "px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none transition-colors"
          }, " Supprimer ")
        ])
      ]),
      _: 1
    }, 8, ["isVisible"]),
    _createVNode(_unref(ModalComp), {
      isVisible: showSendModal.value,
      title: "Envoyer l'email promotionnel",
      size: "sm",
      "onUpdate:isVisible": _cache[3] || (_cache[3] = ($event: any) => (showSendModal.value = false))
    }, {
      body: _withCtx(() => [
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("p", null, [
            _cache[14] || (_cache[14] = _createTextVNode(" Confirmer l'envoi de l'email promotionnel à ")),
            _createElementVNode("strong", null, _toDisplayString(promoToSend.value?.acheteur), 1),
            _cache[15] || (_cache[15] = _createTextVNode(" (")),
            _createElementVNode("span", _hoisted_14, _toDisplayString(promoToSend.value?.buyerEmail), 1),
            _cache[16] || (_cache[16] = _createTextVNode(") ? "))
          ])
        ])
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_15, [
          _createElementVNode("button", {
            onClick: _cache[2] || (_cache[2] = ($event: any) => (showSendModal.value = false)),
            type: "button",
            class: _normalizeClass(["px-4 py-2 text-black bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none transition-colors", isSendingEmail.value ? 'opacity-60 cursor-not-allowed' : '']),
            disabled: isSendingEmail.value
          }, " Annuler ", 10, _hoisted_16),
          _createElementVNode("button", {
            onClick: confirmSend,
            type: "button",
            class: _normalizeClass(["px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none transition-colors inline-flex items-center gap-2", isSendingEmail.value ? 'opacity-60 cursor-not-allowed' : '']),
            disabled: isSendingEmail.value
          }, [
            (isSendingEmail.value)
              ? (_openBlock(), _createElementBlock("svg", _hoisted_18, _cache[17] || (_cache[17] = [
                  _createElementVNode("circle", {
                    class: "opacity-25",
                    cx: "12",
                    cy: "12",
                    r: "10",
                    stroke: "currentColor",
                    "stroke-width": "4"
                  }, null, -1),
                  _createElementVNode("path", {
                    class: "opacity-75",
                    fill: "currentColor",
                    d: "M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  }, null, -1)
                ])))
              : _createCommentVNode("", true),
            _createElementVNode("span", null, _toDisplayString(isSendingEmail.value ? 'Envoi…' : 'Envoyer'), 1)
          ], 10, _hoisted_17)
        ])
      ]),
      _: 1
    }, 8, ["isVisible"])
  ], 64))
}
}

})